import * as React from 'react';
import { CustomerAgreementInput, CustomersFallingBack } from '../types';

type SaveInput = CustomerAgreementInput & { isActive?: boolean };
import { useCreateCustomerAgreementMutation } from '../queries/useCreateCustomerAgreementMutation';
import { useUpdateCustomerAgreementMutation } from '../queries/useUpdateCustomerAgreementMutation';
import { readValidationMessages, ValidationMessage } from '../../../shared/validationErrors';
import { useTelemetryContext } from '../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../telemetry/utils';
import { useUserContext } from '../../../shared/context/user-context';

export type SaveCustomerAgreementSuccess = {
    name: string;
    created: boolean;
    customersFallingBack: CustomersFallingBack | null;
};

export type SaveCustomerAgreementFailure = {
    name: string;
    messages: ValidationMessage[];
};

type SaveCustomerAgreementState = {
    saveCustomerAgreement: (id: number | null, input: SaveInput) => Promise<boolean>;
    saveError: SaveCustomerAgreementFailure | null;
    dismissSaveError: () => void;
    saveSuccess: SaveCustomerAgreementSuccess | null;
    dismissSaveSuccess: () => void;
};

export function useSaveCustomerAgreement(): SaveCustomerAgreementState {
    const { user } = useUserContext();
    const { logger } = useTelemetryContext();
    const createCustomerAgreement = useCreateCustomerAgreementMutation();
    const updateCustomerAgreement = useUpdateCustomerAgreementMutation();
    const [saveError, setSaveError] = React.useState<SaveCustomerAgreementFailure | null>(null);
    const [saveSuccess, setSaveSuccess] = React.useState<SaveCustomerAgreementSuccess | null>(null);

    async function saveCustomerAgreement(id: number | null, input: SaveInput): Promise<boolean> {
        try {
            const saved =
                id === null
                    ? await createCustomerAgreement(input)
                    : await updateCustomerAgreement(id, {
                          ...input,
                          isActive: input.isActive !== false
                      });
            setSaveSuccess({
                name: saved.name,
                created: id === null,
                customersFallingBack: saved.customersFallingBack || null
            });
            return true;
        } catch (error) {
            logger.logException(error, {
                origin: 'CustomerAgreements.SaveCustomerAgreement',
                userInfo: getUserInfo(user)
            });
            setSaveError({ name: input.name, messages: await readValidationMessages(error) });
            return false;
        }
    }

    function dismissSaveError(): void {
        setSaveError(null);
    }

    function dismissSaveSuccess(): void {
        setSaveSuccess(null);
    }

    return { saveCustomerAgreement, saveError, dismissSaveError, saveSuccess, dismissSaveSuccess };
}
